import { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { head, last } from 'ramda';
import { Search } from 'components/icons';
import { cn } from 'lib/utils';
import { useSearchSuggestions } from 'hooks';
import { usePhrRouteMatch } from 'my-phr/layout/hooks';
import SecondaryTarget from './SecondaryTarget';
import { useGlobalSearch } from './GlobalSeachProvider';
import { useSearchTargets } from './hooks';
import PrimaryTarget from './PrimaryTarget';
import { TAB_TYPES } from './const';

const SUGGESTIONS_DEBOUCE = 1000;

const isElementVisibleInContainer = (ele, container) => {
  if (!ele || !container) return false;
  const rect = ele.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  return (
    rect.top >= containerRect.top &&
    rect.left >= containerRect.left &&
    rect.bottom <= containerRect.bottom &&
    rect.right <= containerRect.right
  );
};

export default function GlobalSearch() {
  const { t } = useTranslation();
  const inputRef = useRef();
  const containerRef = useRef();

  const { isActive, onClose, onSubmit } = useGlobalSearch();
  const [currentQuery, setCurrentQuery] = useState('');
  const [currentSelection, setCurrentSelection] = useState();

  const { tabs, next, previous, appendTabs } =
    useSearchTargets(currentSelection);

  const refMap = useMemo(() => {
    return tabs.reduce((acc, tab, index) => {
      acc[tab.id] = { index, ref: createRef() };
      return acc;
    }, {});
  }, [tabs]);

  const { fetch, clear: clearSuggestionsSearch } = useSearchSuggestions(
    SUGGESTIONS_DEBOUCE,
    null,
    {},
    {
      enabled: currentQuery.length > 0,
      onSuccess: (suggestions) => {
        const additionalTabs = suggestions?.map((suggestion) => ({
          label: suggestion?.name,
          id: `${suggestion?.name}`,
        }));
        appendTabs(additionalTabs);
      },
    }
  );

  useEffect(() => {
    if (isActive) {
      inputRef.current?.focus();
    } else {
      setCurrentQuery('');
      setCurrentSelection();
      clearSuggestionsSearch();
    }
  }, [isActive, clearSuggestionsSearch]);

  const handleQueryChange = (query) => {
    setCurrentQuery(query);
    fetch({ value: query });
  };

  const handleArrowKey = (direction) => {
    const nextId = direction === 'up' ? previous : next;
    setCurrentSelection((prev) => {
      const newSelection = prev
        ? nextId
        : direction === 'up'
        ? last(tabs)?.id
        : head(tabs)?.id;
      const targetRef = refMap[newSelection]?.ref?.current;
      if (
        targetRef &&
        !isElementVisibleInContainer(targetRef, containerRef.current)
      ) {
        targetRef.scrollIntoView({ block: 'nearest' });
      }
      return newSelection;
    });
  };

  const handleKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        handleArrowKey('up');
        break;
      case 'ArrowDown':
        e.preventDefault();
        handleArrowKey('down');
        break;
      case 'Escape':
        currentSelection ? setCurrentSelection() : onClose();
        break;
      case 'Enter':
        onSubmit({
          section: currentSelection ?? head(tabs).id,
          query: currentQuery,
        });
        break;
      default:
        break;
    }
  };

  const handleTargetClick = (section, isSuggestion) => {
    const targetSection = isSuggestion ? 'library' : section;
    onSubmit({ section: targetSection, query: currentQuery });
  };

  const primaryTabs = tabs.filter((tab) => tab.type === TAB_TYPES.primary);
  const secondaryTabs = tabs.filter((tab) => tab.type === TAB_TYPES.secondary);
  const hasValue = !!currentQuery;

  const { isTimeline, isDashboard } = usePhrRouteMatch();

  const searchInputPlaceholder = t(
    isTimeline
      ? 'global-search.timeline-placeholder'
      : isDashboard
      ? 'global-search.dashboard-placeholder'
      : 'global-search.library-placeholder'
  );

  if (!isActive) return null;

  return (
    <>
      <div className="fixed top-0 isolate z-[100] flex w-full items-center justify-center bg-white p-4 md:px-2 md:py-8">
        <div className="relative w-full max-w-2xl border">
          <div className="has-[:focus]:smd-input-focus-within-primary flex rounded-sm border border-smd-gray-lighter has-[:focus]:border-smd">
            <input
              ref={inputRef}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={handleKeyDown}
              value={currentQuery}
              placeholder={searchInputPlaceholder}
              className="w-full border-none focus:outline-none focus:ring-0"
            />
            <button className="bg-smd-accent px-3 text-center text-white">
              <Search className="h-5 w-5" />
            </button>
          </div>

          <div
            className={cn(
              'absolute mt-1.5 flex w-full flex-col divide-y rounded-bl-md rounded-br-md bg-white shadow-lg',
              hasValue ? 'animate-fade-in-down' : 'hidden'
            )}
          >
            {primaryTabs.map((tab) => (
              <PrimaryTarget
                key={tab.id}
                id={tab.id}
                ref={refMap[tab.id].ref}
                label={t(tab.labelKey)}
                query={currentQuery}
                isPreSelected={tab.id === currentSelection}
                onTargetClick={handleTargetClick}
                setCurrentSelection={setCurrentSelection}
              />
            ))}

            {secondaryTabs.length > 0 && (
              <div className="flex flex-col">
                <div className="px-4 pb-1 pt-4 font-bold">
                  {t('common.library')}
                </div>
                <div
                  ref={containerRef}
                  className="flex max-h-[180px] flex-col overflow-y-auto"
                >
                  {secondaryTabs.map((tab) => (
                    <SecondaryTarget
                      key={tab.id}
                      ref={refMap[tab.id].ref}
                      id={tab.id}
                      label={tab.label}
                      query={currentQuery}
                      className={cn(
                        'block w-full px-4 py-3 hover:cursor-pointer hover:bg-smd-gray-lighter',
                        tab.id === currentSelection ? 'bg-smd-gray-lighter' : ''
                      )}
                      onTargetClick={handleTargetClick}
                      setCurrentSelection={setCurrentSelection}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black bg-opacity-70 px-4 py-2"
      />
    </>
  );
}
